window._ = require('lodash');

import axios from 'axios';
import store from "./store";
import {app} from "./app";

window.axios = require('axios');

const AUTH_ERROR_MESSAGE = 'Tu sesion no es valida. Inicia sesion nuevamente.';
let lastConnectionToastAt = 0;
let lastRateLimitToastAt = 0;
const PUBLIC_API_PATTERNS = [
    /^\/api\/legal$/,
    /^\/api\/banks(\/|$)/,
    /^\/api\/currencies(\/|$)/,
    /^\/api\/nickname\/check$/,
    /^\/api\/auth\/challenge(\/|$)/,
    /^\/api\/email\/verification\/resend$/,
    /^\/api\/identity\/didit\/webhook$/,
    /^\/api\/identity\/didit\/callback$/,
    /^\/api\/login$/,
    /^\/api\/login\/2fa$/,
    /^\/api\/login\/2fa\/email$/,
    /^\/api\/login\/2fa\/passkey\/options$/,
    /^\/api\/login\/2fa\/passkey\/verify$/,
    /^\/api\/login\/passkey\/options$/,
    /^\/api\/login\/passkey\/verify$/,
    /^\/api\/login\/refresh$/,
    /^\/api\/security\/dynamic-key\/device$/,
    /^\/api\/register$/,
    /^\/api\/password\/email$/,
    /^\/api\/password\/reset$/,
    /^\/api\/auth(\/|$)/
];

axios.interceptors.request.use(config => {
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    appendDeviceHeaders(config.headers);

    const token = store.getters['token'];
    if (token) {
        config.headers['Authorization'] = 'Bearer ' + token;
        const lastActivityAt = window.sessionStorage.getItem('valzum_idle_last_activity_at');
        if (lastActivityAt) {
            config.headers['X-Valzum-Last-Activity-At'] = lastActivityAt;
        }
    } else if (isProtectedApiRequest(config)) {
        store.dispatch('destroy');
        redirectToLogin();
        return Promise.reject(authRequestError(config));
    }

    return config;
}, error => {
    return Promise.reject(error);
});

axios.interceptors.response.use(response => {
    return response;
}, async error => {
    if (!error.response) {
        notifyConnectionIssue(error);
        return Promise.reject(error);
    }

    if (store.getters['token'] && isExpiredTokenResponse(error) && !isRefreshRequest(error.config)) {
        try {
            error.config.__isRetryRequest = true;
            const { data } = await axios.post('/api/login/refresh');
            await store.dispatch('saveToken', data);
            error.config.headers = error.config.headers || {};
            error.config.headers['Authorization'] = 'Bearer ' + data.token;

            return axios.request(error.config);
        } catch (refreshError) {
            await store.dispatch('destroy');
            redirectToLogin();

            return Promise.reject(refreshError);
        }
    }

    if (isAuthFailureResponse(error)) {
        await store.dispatch('destroy');
        redirectToLogin();
        return Promise.reject(error);
    }

    if (error.response.status === 429) {
        const now = Date.now();
        if (now - lastRateLimitToastAt >= 20000) {
            lastRateLimitToastAt = now;
            const retryAfter = Number(error.response.headers && error.response.headers['retry-after'] ? error.response.headers['retry-after'] : 0);
            const waitText = retryAfter > 0 ? ` Espera ${retryAfter} segundos e intenta de nuevo.` : ' Intenta de nuevo en un momento.';
            app.$toast.warning('Hay demasiadas solicitudes en este momento.' + waitText, { icon: 'mdi-timer-alert' });
        }

        return Promise.reject(error);
    }

    const responseData = error.response.data || {};

    if (error.response.status === 423 && responseData.code === 'session_confirmation_required') {
        try {
            window.sessionStorage.setItem('valzum_staff_idle_locked', '1');
            if (typeof window.valzumRequireSessionConfirmation === 'function') {
                window.valzumRequireSessionConfirmation();
            }
        } catch (e) {}

        return Promise.reject(error);
    }

    const hasValidationErrors = error.response.status === 422 && responseData.errors;

    if (!hasValidationErrors && responseData.message !== undefined) {
        app.$toast.error(responseData.message || 'Algo salió mal.', { icon: 'mdi-close-circle' });
    }

    if (responseData.error !== undefined) {
        app.$toast.error(responseData.error || 'Se produjo un error.', { icon: 'mdi-close-circle' });
    }

    return Promise.reject(error);
});

function authRequestError(config) {
    const error = new Error(AUTH_ERROR_MESSAGE);
    error.config = config;
    error.response = {
        status: 401,
        data: {
            message: AUTH_ERROR_MESSAGE,
            errors: {}
        },
        config: config
    };
    error.isMissingAuthToken = true;

    return error;
}

function notifyConnectionIssue(error) {
    if (isSilentConnectionError(error)) {
        return;
    }

    const now = Date.now();
    if (now - lastConnectionToastAt < 20000) {
        return;
    }

    lastConnectionToastAt = now;
    app.$toast.error(connectionIssueText(), { icon: 'mdi-wifi-alert' });
}

function isSilentConnectionError(error) {
    const message = String(error && error.message ? error.message : '').toLowerCase();
    const code = String(error && error.code ? error.code : '').toUpperCase();

    return (axios.isCancel && axios.isCancel(error))
        || code === 'ERR_CANCELED'
        || message.indexOf('cancel') !== -1
        || message.indexOf('abort') !== -1;
}

function connectionIssueText() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        return 'No tienes conexion a internet.';
    }

    return 'No recibimos respuesta. Revisa tu conexion e intenta de nuevo.';
}

function appendDeviceHeaders(headers) {
    const cfg = window.config || {};
    const deviceUid = cfg.device_uid || browserDeviceUid();
    const deviceUidSource = cfg.device_uid_source || (deviceUid ? 'browser' : null);
    const values = {
        'X-Valzum-Device': cfg.device,
        'X-Valzum-Product': cfg.name,
        'X-Valzum-App': cfg.name,
        'X-Valzum-Version': cfg.version,
        'X-Valzum-Build': cfg.build,
        'X-Valzum-Ios-Id': cfg.ios_id,
        'X-Valzum-Device-Uid': deviceUid,
        'X-Valzum-Device-Id': deviceUid,
        'X-Valzum-Device-Source': deviceUidSource
    };

    Object.keys(values).forEach(key => {
        if (values[key] !== undefined && values[key] !== null && values[key] !== '') {
            headers[key] = values[key];
        }
    });
}

function browserDeviceUid() {
    try {
        if (!window.localStorage) {
            return null;
        }

        const key = 'valzum_browser_device_uid';
        let value = window.localStorage.getItem(key);

        if (!value) {
            const random = window.crypto && typeof window.crypto.getRandomValues === 'function'
                ? Array.from(window.crypto.getRandomValues(new Uint32Array(4))).map(part => part.toString(16)).join('')
                : String(Date.now()) + String(Math.random()).replace(/\D/g, '');
            value = 'web-' + random;
            window.localStorage.setItem(key, value);
        }

        return value;
    } catch (error) {
        return null;
    }
}

function apiPath(config) {
    const url = config && config.url ? String(config.url) : '';

    if (!url) {
        return '';
    }

    try {
        return new URL(url, window.location.origin).pathname;
    } catch (e) {
        return url.split('?')[0];
    }
}

function isApiRequest(config) {
    return apiPath(config).indexOf('/api/') === 0;
}

function isPublicApiRequest(config) {
    const path = apiPath(config);

    return PUBLIC_API_PATTERNS.some(pattern => pattern.test(path));
}

function isProtectedApiRequest(config) {
    return isApiRequest(config) && !isPublicApiRequest(config);
}

function isRefreshRequest(config) {
    return apiPath(config) === '/api/login/refresh' || !!(config && config.__isRetryRequest);
}

function responseMessage(error) {
    return String(error && error.response && error.response.data && error.response.data.message ? error.response.data.message : '');
}

function isExpiredTokenResponse(error) {
    if (!error || !error.response || error.response.status !== 401) {
        return false;
    }

    const message = responseMessage(error).toLowerCase();

    return message === 'el token ha caducado.' || message === 'token has expired';
}

function isAuthFailureResponse(error) {
    if (!error || !error.response || error.response.status !== 401 || !isProtectedApiRequest(error.config)) {
        return false;
    }

    const message = responseMessage(error).toLowerCase();

    return error.isMissingAuthToken
        || message === AUTH_ERROR_MESSAGE.toLowerCase()
        || message === 'token not provided'
        || message === 'user not found'
        || message === 'el token ha caducado y no se pudo actualizar.'
        || message === 'el token ha sido incluido en la lista negra.'
        || message === 'the token has been blacklisted';
}

function redirectToLogin() {
    const router = app && app.$router ? app.$router : null;

    if (!router) {
        if (window.location.pathname !== '/login') {
            window.location.href = '/login';
        }
        return;
    }

    if (router.currentRoute && router.currentRoute.name === 'Login') {
        return;
    }

    router.push({ name: 'Login' }).catch(() => {});
}
